import type { ModuleConfig } from "../moduleConfig.types";
import {
  BRANDS,
  MARKETS,
  FILTERS,
  SEGMENTS,
  PERIOD_TYPE,
  PERIOD,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  AGE_START,
  AGE_END,
  METRIC,
  RESPONSE,
  TRENDS_METRIC,
  TRENDS_RESPONSE,
  GROUPED_BY,
  QUESTION_OPTIONS,
  VIEW_ID,
} from "@/constant/constantsThrougoutProject";

/**
 * Hierarchical screen config for the Exclusive Questions module.
 *
 * Resolution order (later layers override earlier ones):
 *   module.config
 *   → screenTypes.config
 *   → screenTypes.[name].config
 *   → screenTypes.[name].modes.config
 *   → screenTypes.[name].modes.[name].config
 *   → screenTypes.[name].modes.[name].visualisationType.config
 *   → screenTypes.[name].modes.[name].visualisationType.[name].config
 *
 * Note: Exclusive Questions is unique in that:
 *   - There is NO Market screen type
 *   - GROUPED_BY and QUESTION_OPTIONS are present in all modes for both screen types
 *   - No PROFILE visualisation type
 *   - By Segment TREND has TRENDS_METRIC and TRENDS_RESPONSE
 */

export const exclusiveQuestionsModuleConfig: ModuleConfig = {
  // ── Module-level base config ──────────────────────────────────────────────
  config: {
    type: "MODULE",
    supportMarkets: true,
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
    supportBase: true,
    defaultBase: "GENPOP",
    // disableBases: ["USER", "ADRECALL", "RQ_GENPOP"]
    supportedBases: ["GENPOP", "FAMILIAR"],
    supportDatePeriod: true,
    supportScoreType: false,
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["BAR", "LINE", "TABLE"],
    defaultVisualisationType: "BAR",
    supportStatTesting: true,
  },

  screenTypes: {
    // ── By Segment ───────────────────────────────────────────────────────────
    SEGMENT: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_ONLY",
        maximumAllowedMarkets: 1,
      },
      modes: {
        TREND: {
          config: {
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            // By Segment TREND includes GROUPED_BY, QUESTION_OPTIONS, TRENDS_METRIC, TRENDS_RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              TRENDS_RESPONSE,
              VIEW_ID,
            ],
          },
        },
        POINT_IN_TIME: {
          config: {
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
          },
        },
      },
    },

    // ── By Brand ─────────────────────────────────────────────────────────────
    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        maximumAllowedMarkets: 1,
      },
      modes: {
        TREND: {
          config: {
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
          },
        },
        POINT_IN_TIME: {
          config: {
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              METRIC,
              RESPONSE,
              VIEW_ID,
            ],
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
          },
        },
      },
    },

    // Note: Exclusive Questions has NO Market screen type
  },
};